import React, { useEffect, useMemo, useState } from 'react';
import { geoPath } from 'd3-geo';
import * as topojson from 'topojson-client';
// @ts-expect-error — topojson-specification ships partial types in this version
import type { Topology } from 'topojson-specification';
import { pickProjection } from './geo';
import { loadAtlas } from './atlasLoader';
export type MapMode = 'display' | 'select-region' | 'select-point' | 'select-city' | 'place-marker' | 'label-regions';
export interface CityMarker {
  id?: string;
  name?: string;
  lat: number;
  lon: number;
}
/**
 * LinePath — a polyline rendered on the map (e.g. river, waterway).
 * Points are in [lon, lat] order to match GeoJSON / d3-geo convention.
 */
export interface LinePath {
  id: string;
  points: [number, number][];
  /** Optional label drawn near the line midpoint (e.g. for feedback) */
  label?: string;
  /** Visual emphasis style. Default: 'default'. */
  style?: 'default' | 'highlight' | 'muted';
}
export interface MapCanvasProps {
  mapId: string;
  mode?: MapMode;
  focusRegionId?: string;
  highlightRegionIds?: string[];
  highlightStyle?: 'primary' | 'secondary' | 'subtle';
  selectedRegionId?: string | null;
  onRegionSelect?: (regionId: string) => void;
  /** select-point mode: callback with WGS84 coordinates of click */
  onPointSelect?: (coord: {
    lat: number;
    lon: number;
  }) => void;
  /** select-point mode: currently placed pin (lat/lon) */
  selectedPoint?: {
    lat: number;
    lon: number;
  } | null;
  /** Optional reference markers (e.g. other cities for orientation) */
  cityMarkers?: CityMarker[];
  /** select-city mode: clickable city dots */
  selectableCities?: CityMarker[];
  selectedCityId?: string | null;
  onCitySelect?: (cityId: string) => void;
  /** select-point mode: target point (only shown after answer for feedback) */
  targetPoint?: {
    lat: number;
    lon: number;
    label?: string;
  } | null;
  /** Numbered placed markers (e.g. for multi-city placement). Rendered in accent color with a number inside. */
  placedMarkers?: Array<{
    lat: number;
    lon: number;
    index: number;
    isActive?: boolean;
  }>;
  /** Polylines (e.g. rivers) rendered as a background layer, non-interactive. */
  linePaths?: LinePath[];
  /**
   * Tap-and-snap callback. When set together with `linePaths` and
   * `mode='select-point'`, taps anywhere on the map are snapped to the
   * NEAREST polyline (in pixel space). The callback receives the chosen
   * line id plus the snapped WGS84 coordinate ON that line. When set,
   * `onPointSelect` is NOT fired — the snap takes precedence so the
   * child gets unambiguous "I picked this river" feedback instead of a
   * dangling pin in empty water.
   */
  onLineSelect?: (lineId: string, snapped: {
    lat: number;
    lon: number;
  }) => void;
  /** Id of the currently selected line — drives highlight/muted styling. */
  selectedLineId?: string | null;
  showFirstLetterHint?: boolean;
  width?: number;
  height?: number;
  ariaLabel?: string;
  moduleColor?: string;
}
const PAPER_BG = '#FAF7F0';
const PAPER_FILL = '#FFFEF8';
const PAPER_BORDER = '#A89F8E';
export function MapCanvas({
  mapId,
  mode = 'display',
  focusRegionId,
  highlightRegionIds = [],
  highlightStyle = 'primary',
  selectedRegionId = null,
  onRegionSelect,
  onPointSelect,
  selectedPoint = null,
  cityMarkers = [],
  selectableCities = [],
  selectedCityId = null,
  onCitySelect,
  targetPoint = null,
  placedMarkers = [],
  linePaths = [],
  onLineSelect,
  selectedLineId = null,
  showFirstLetterHint = false,
  width = 600,
  height = 600,
  ariaLabel,
  moduleColor
}: MapCanvasProps) {
  const [topology, setTopology] = useState<Topology | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setTopology(null);
    setError(null);
    loadAtlas(mapId).then(t => {
      if (!cancelled) setTopology(t as Topology);
    }).catch(err => {
      if (!cancelled) setError(err.message);
    });
    return () => {
      cancelled = true;
    };
  }, [mapId]);
  const projection = useMemo(() => {
    const proj = pickProjection(mapId, width, height);
    if (!topology || !topology.objects?.regions) return proj;
    // @ts-expect-error — topojson.feature() return type is not narrowed to a GeoJSON FeatureCollection
    const geojson = topojson.feature(topology, topology.objects.regions);
    if (focusRegionId) {
      // @ts-expect-error — geojson.features is typed as any[] after the unsafe feature() call
      const feature = geojson.features?.find((f: any) => f.id === focusRegionId);
      if (feature) {
        proj.fitExtent([[20, 20], [width - 20, height - 20]], feature);
        return proj;
      }
    }
    // For atlases that mix interactive regions with reference geometry
    // (e.g. nl-islands has the 5 islands + mainland reference polygons),
    // auto-fit ONLY the interactive regions so the islands fill the canvas.
    // Detected via properties.isReference === true on reference features.
    // @ts-expect-error — geojson.features is typed as any[] after the unsafe feature() call
    const interactive = (geojson.features || []).filter(
    // @ts-expect-error — inner feature param has no GeoJSON Feature typing
    (f: any) => f?.properties?.isReference !== true);
    // @ts-expect-error — geojson.features is typed as any[] after the unsafe feature() call
    const hasReference = (geojson.features || []).some(
    // @ts-expect-error — inner feature param has no GeoJSON Feature typing
    (f: any) => f?.properties?.isReference === true);
    if (hasReference && interactive.length > 0) {
      proj.fitExtent([[24, 24], [width - 24, height - 24]],
      // @ts-expect-error — d3-geo accepts a FeatureCollection here
      {
        type: 'FeatureCollection',
        features: interactive
      });
    }
    return proj;
  }, [mapId, width, height, focusRegionId, topology]);
  const pathGenerator = useMemo(() => geoPath(projection), [projection]);
  const features = useMemo(() => {
    if (!topology || !topology.objects?.regions) return [];
    // @ts-expect-error — topojson.feature() return type is not narrowed to a GeoJSON FeatureCollection
    const geojson = topojson.feature(topology, topology.objects.regions);
    // @ts-expect-error — geojson.features inherits the same unnarrowed return type
    return geojson.features || [];
  }, [topology]);
  // NOTE: projectedLinePaths is declared HERE (before any conditional early
  // returns below) to keep hook order stable across renders. Moving it
  // below the `if (!topology) return` guard caused a Rules-of-Hooks
  // violation that crashed snap-to-dot map questions.
  //
  // Each entry now carries BOTH the SVG `d` string (for rendering) AND the
  // raw projected pixel coordinates (for snap-to-line distance math). The
  // raw points are how findNearestLineSnap finds the closest segment when
  // the user taps the map in tap-and-snap mode.
  const projectedLinePaths = useMemo(() => {
    return linePaths.map(line => {
      const projected: [number, number][] = [];
      for (const [lon, lat] of line.points) {
        const p = projection([lon, lat]);
        if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
        projected.push([p[0], p[1]]);
      }
      if (projected.length < 2) return null;
      const d = `M ${projected.map(([x, y]) => `${x},${y}`).join(' L ')}`;
      return {
        id: line.id,
        d,
        xy: projected,
        style: line.style ?? 'default',
        label: line.label
      };
    }).filter((x): x is {
      id: string;
      d: string;
      xy: [number, number][];
      style: 'default' | 'highlight' | 'muted';
      label?: string;
    } => x !== null);
  }, [linePaths, projection]);
  const isRegionInteractive = mode === 'select-region';
  const isPointInteractive = mode === 'select-point';
  if (error) {
    return <div className="flex items-center justify-center bg-red-50 text-red-600 p-4 rounded-2xl border border-red-200" style={{
      width,
      height
    }} role="alert" data-id="element-13243">
        Kaart laden mislukt: {error}
      </div>;
  }
  if (!topology) {
    return <div className="flex items-center justify-center bg-stone-50 text-stone-400 p-4 rounded-2xl border border-stone-200 animate-pulse" style={{
      width,
      height
    }} role="status" aria-label="Kaart laden..." data-id="element-13244">
        Kaart laden...
      </div>;
  }
  const accentColor = moduleColor || '#8B5CF6';
  const getHighlightFill = () => {
    switch (highlightStyle) {
      case 'primary':
        return hexToRgba(accentColor, 0.35);
      case 'secondary':
        return 'rgba(34, 197, 94, 0.4)';
      case 'subtle':
        return 'rgba(0, 0, 0, 0.08)';
      default:
        return hexToRgba(accentColor, 0.35);
    }
  };
  const handleRegionClick = (regionId: string) => {
    if (!isRegionInteractive || !onRegionSelect) return;
    onRegionSelect(regionId);
  };
  const handleKeyDown = (e: React.KeyboardEvent<SVGPathElement>, regionId: string) => {
    if (!isRegionInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRegionClick(regionId);
    }
  };
  // Convert SVG click position → WGS84 lat/lon via projection.invert.
  // Tap-and-snap: when onLineSelect is provided AND polylines exist, snap
  // the click to the nearest river and emit that instead of a free point.
  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isPointInteractive) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * width;
    const y = (e.clientY - rect.top) / rect.height * height;
    // Snap-to-line path
    if (onLineSelect && projectedLinePaths.length > 0) {
      const snap = findNearestLineSnap(x, y, projectedLinePaths);
      if (snap) {
        const inv = (projection as any).invert?.([snap.x, snap.y]);
        if (inv && Number.isFinite(inv[0]) && Number.isFinite(inv[1])) {
          onLineSelect(snap.lineId, {
            lat: inv[1],
            lon: inv[0]
          });
          return;
        }
      }
    }
    // Fallback: free-point select
    if (!onPointSelect) return;
    const inv = (projection as any).invert?.([x, y]);
    if (inv && Number.isFinite(inv[0]) && Number.isFinite(inv[1])) {
      onPointSelect({
        lat: inv[1],
        lon: inv[0]
      });
    }
  };
  const projectPoint = (lat: number, lon: number): [number, number] | null => {
    const p = projection([lon, lat]);
    if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null;
    return [p[0], p[1]];
  };
  const selectedXY = selectedPoint ? projectPoint(selectedPoint.lat, selectedPoint.lon) : null;
  const targetXY = targetPoint ? projectPoint(targetPoint.lat, targetPoint.lon) : null;
  return <div className="relative overflow-hidden rounded-2xl border-2 shadow-sm" style={{
    width,
    height,
    backgroundColor: PAPER_BG,
    borderColor: hexToRgba(PAPER_BORDER, 0.4)
  }} data-id="element-13245">
      <svg width={width} height={height} role="img" aria-label={ariaLabel || `Kaart: ${mapId}`} className="w-full h-full" style={{
      cursor: isPointInteractive ? 'crosshair' : 'default'
    }} onClick={handleSvgClick} data-id="element-13246">
        <g data-id="element-13247">
          {features.map((feature: any, i: number) => {
          const id: string = feature.id ?? `region-${i}`;
          const name: string = feature.properties?.name ?? id;
          const isReference = feature.properties?.isReference === true;
          const isInteractiveHere = isRegionInteractive && !isReference;
          const isSelected = isInteractiveHere && selectedRegionId === id;
          const isStaticHighlighted = !isRegionInteractive && highlightRegionIds.includes(id);
          const isHovered = isInteractiveHere && hoveredRegionId === id;
          const isFocused = focusRegionId ? id === focusRegionId : true;
          let fill: string = PAPER_FILL;
          if (isReference) fill = '#F0EBE0';else if (isSelected) fill = hexToRgba(accentColor, 0.5);else if (isStaticHighlighted) fill = getHighlightFill();else if (isHovered) fill = hexToRgba(accentColor, 0.15);
          const stroke = isSelected ? accentColor : isReference ? hexToRgba(PAPER_BORDER, 0.5) : PAPER_BORDER;
          const strokeWidth = isSelected ? 2.5 : isReference ? 1 : 1.5;
          const path = pathGenerator(feature) || '';
          const centroid = pathGenerator.centroid(feature);
          const showHint = showFirstLetterHint && !isSelected && !isReference && name.length > 0;
          return <g key={id} data-id="element-13248">
                <path d={path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} className="transition-colors duration-200" style={{
              cursor: isInteractiveHere ? 'pointer' : 'inherit',
              outline: 'none',
              pointerEvents: isReference || isPointInteractive || mode === 'select-city' ? 'none' : 'auto',
              opacity: isFocused ? 1 : 0.3
            }} tabIndex={isInteractiveHere ? 0 : -1} role={isInteractiveHere ? 'button' : undefined} aria-label={isInteractiveHere ? name : undefined} aria-pressed={isInteractiveHere ? isSelected : undefined} onClick={() => !isReference && handleRegionClick(id)} onMouseEnter={() => isInteractiveHere && setHoveredRegionId(id)} onMouseLeave={() => isInteractiveHere && setHoveredRegionId(prev => prev === id ? null : prev)} onKeyDown={e => handleKeyDown(e, id)} data-id="element-13249" />
                {showHint && Number.isFinite(centroid[0]) && <text x={centroid[0]} y={centroid[1]} textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600} fill="#6B6056" pointerEvents="none" data-id="element-13250">
                    {name.charAt(0).toUpperCase()}
                  </text>}
              </g>;
        })}

          {/* River / waterway polylines (rendered AFTER regions so they sit on
           top of the paper polygons but BEFORE markers/pins). Pointer-events
           none so the SVG-level click handler can do tap-and-snap routing.
           Visual hierarchy (softer overall — was too dominant):
           - default  : opacity 0.5, 2px, slate-blue. Reads as water, not as
                      a competing UI element.
           - muted    : opacity 0.18, 1.75px. Used for OTHER rivers once one
                      is selected, so the chosen one stands out.
           - highlight: moduleColor accent, 4.5px, plus a 12px outer glow at
                      opacity 0.2. Same "I picked this" affordance as
                      selected regions/cities elsewhere on the canvas. */}
          {projectedLinePaths.map(line => {
          // Auto-derive style from selectedLineId when consumer didn't
          // explicitly override per-line (i.e. style === 'default').
          let effectiveStyle = line.style;
          if (selectedLineId && line.style === 'default') {
            effectiveStyle = line.id === selectedLineId ? 'highlight' : 'muted';
          }
          const isHighlight = effectiveStyle === 'highlight';
          const isMuted = effectiveStyle === 'muted';
          const defaultStroke = '#2563EB';
          const highlightStroke = accentColor;
          const stroke = isHighlight ? highlightStroke : isMuted ? defaultStroke : defaultStroke;
          const strokeWidth = isHighlight ? 4.5 : isMuted ? 1.75 : 2;
          const opacity = isHighlight ? 1 : isMuted ? 0.18 : 0.5;
          return <g key={`line-${line.id}`} data-id="element-13251">
                {/* Outer glow for highlighted line — duplicate path with wide,
                  low-opacity stroke so the selected river feels lit-up
                  without dominating the whole map. */}
                {isHighlight && <path d={line.d} fill="none" stroke={highlightStroke} strokeWidth={12} strokeLinecap="round" strokeLinejoin="round" opacity={0.22} pointerEvents="none" data-id="element-13252" />}
                {/* Invisible hit-area kept as a no-op visual; the actual
                  snap-to-line routing happens in handleSvgClick using the
                  projected pixel coordinates. */}
                <path d={line.d} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={opacity} pointerEvents="none" className="transition-opacity duration-200" data-id="element-13253" />
              </g>;
        })}

          {/* Selectable city markers (select-city mode) — NO name labels to prevent answer leak */}
          {mode === 'select-city' && selectableCities?.map((c, i) => {
          const xy = projectPoint(c.lat, c.lon);
          if (!xy) return null;
          const isSelected = selectedCityId === c.id;
          return <g key={`select-city-${c.id ?? i}`} transform={`translate(${xy[0]}, ${xy[1]})`} style={{
            cursor: 'pointer',
            pointerEvents: 'auto'
          }} onClick={e => {
            e.stopPropagation();
            if (c.id) onCitySelect?.(c.id);
          }} role="button" aria-label={`Stad ${i + 1}`} aria-pressed={isSelected} data-id="element-13254">
                  {/* Larger transparent hit-area for easier tapping */}
                  <circle r={18} fill="transparent" data-id="element-13255" />
                  {/* Outer ring on selection */}
                  {isSelected && <circle r={14} fill={hexToRgba(accentColor, 0.18)} stroke={accentColor} strokeWidth={2} className="transition-all duration-200" data-id="element-13256" />}
                  <circle r={isSelected ? 7 : 6} fill={isSelected ? accentColor : PAPER_FILL} stroke={accentColor} strokeWidth={2} className="transition-all duration-200" data-id="element-13257" />
                </g>;
        })}

          {/* City reference markers (orientation pins) */}
          {cityMarkers.map((c, i) => {
          const xy = projectPoint(c.lat, c.lon);
          if (!xy) return null;
          return <g key={`city-${c.id ?? i}`} pointerEvents="none" transform={`translate(${xy[0]}, ${xy[1]})`} data-id="element-13258">
                <circle r={3} fill="#6B6056" stroke="#FFFEF8" strokeWidth={1} data-id="element-13259" />
                {c.name && <text x={5} y={3} fontSize={10} fill="#6B6056" fontWeight={500} data-id="element-13260">
                    {c.name}
                  </text>}
              </g>;
        })}

          {/* Numbered placed markers (e.g. multi-city placement) */}
          {placedMarkers.map((m, i) => {
          const xy = projectPoint(m.lat, m.lon);
          if (!xy) return null;
          return <g key={`placed-${i}`} pointerEvents="none" transform={`translate(${xy[0]}, ${xy[1]})`} data-id="element-13261">
                {m.isActive && <circle r={14} fill={hexToRgba(accentColor, 0.18)} stroke={accentColor} strokeWidth={2} data-id="element-13262" />}
                <circle r={11} fill={accentColor} stroke="#FFFEF8" strokeWidth={2} data-id="element-13263" />
                <text textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700} fill="#FFFEF8" data-id="element-13264">
                  {m.index}
                </text>
              </g>;
        })}

          {/* User-placed pin (select-point mode) */}
          {selectedXY && <g pointerEvents="none" transform={`translate(${selectedXY[0]}, ${selectedXY[1]})`} data-id="element-13265">
              <circle r={10} fill={hexToRgba(accentColor, 0.25)} stroke={accentColor} strokeWidth={2} data-id="element-13266" />
              <circle r={4} fill={accentColor} data-id="element-13267" />
            </g>}

          {/* Target point reveal (after answer) */}
          {targetXY && <g pointerEvents="none" transform={`translate(${targetXY[0]}, ${targetXY[1]})`} data-id="element-13268">
              <circle r={8} fill="rgba(34,197,94,0.3)" stroke="#22C55E" strokeWidth={2} data-id="element-13269" />
              <circle r={3} fill="#22C55E" data-id="element-13270" />
              {targetPoint?.label && <text x={10} y={4} fontSize={11} fontWeight={600} fill="#15803D" data-id="element-13271">
                  {targetPoint.label}
                </text>}
            </g>}
        </g>
      </svg>
    </div>;
}
// ── Helpers ──────────────────────────────────────────────────
/**
 * Tap-and-snap geometry: given a click in SVG pixel space and a set of
 * projected polylines, return the id of the nearest line plus the snapped
 * point on that line. Returns null if no lines were within reach.
 *
 * Uses standard point-to-segment projection: for each segment AB, project
 * the click P onto AB clamped to [0, 1] along the segment, then compare
 * Euclidean distance. The nearest match across ALL segments of ALL lines
 * wins. Threshold guards against absurd taps (e.g. a corner of the canvas
 * far from any river) — kept generous (~60px) so kids don't have to be
 * precise.
 */
function findNearestLineSnap(px: number, py: number, lines: Array<{
  id: string;
  xy: [number, number][];
}>): {
  lineId: string;
  x: number;
  y: number;
  distance: number;
} | null {
  let best: {
    lineId: string;
    x: number;
    y: number;
    distance: number;
  } | null = null;
  const MAX_SNAP_PX = 60;
  for (const line of lines) {
    const pts = line.xy;
    for (let i = 0; i < pts.length - 1; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[i + 1];
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) continue;
      let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
      if (t < 0) t = 0;else if (t > 1) t = 1;
      const cx = ax + t * dx;
      const cy = ay + t * dy;
      const ddx = px - cx;
      const ddy = py - cy;
      const dist = Math.sqrt(ddx * ddx + ddy * ddy);
      if (dist > MAX_SNAP_PX) continue;
      if (!best || dist < best.distance) {
        best = {
          lineId: line.id,
          x: cx,
          y: cy,
          distance: dist
        };
      }
    }
  }
  return best;
}
function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  const full = cleaned.length === 3 ? cleaned.split('').map(c => c + c).join('') : cleaned;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return `rgba(139, 92, 246, ${alpha})`;
  const r = num >> 16 & 255;
  const g = num >> 8 & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}